import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, CardContent } from '@feathr/components';

import type { TAnhingaEnvProfile } from './AnhingaEnvSwitcher.hooks';

interface IAnhingaEnvProfileRowProps {
  /** The profile this row represents. */
  profile: TAnhingaEnvProfile;
  /** Human-readable label for the profile. */
  label: string;
  /** Whether the backend is currently running this profile. */
  isCurrent: boolean;
  /** Whether any switch is in progress (disables all rows). */
  isSwitching: boolean;
  /** Whether the in-progress switch targets this row. */
  isSwitchingThis: boolean;
  /** Called with this row's profile when its button is pressed. */
  onSelect: (profile: TAnhingaEnvProfile) => void;
}

/**
 * A single environment profile row on the anhinga env switcher card: shows the
 * profile name and either a "Current" indicator or a switch button.
 */
function AnhingaEnvProfileRow({
  profile,
  label,
  isCurrent,
  isSwitching,
  isSwitchingThis,
  onSelect,
}: Readonly<IAnhingaEnvProfileRowProps>): JSX.Element {
  const { t } = useTranslation();

  function handleClick(): void {
    onSelect(profile);
  }

  return (
    <CardContent
      action={
        <Button
          aria-label={isCurrent ? undefined : t('Switch to {{label}}', { label })}
          disabled={isCurrent || isSwitching}
          isLoading={isSwitchingThis}
          onClick={handleClick}
          type={isCurrent ? 'secondary' : 'primary'}
        >
          {isCurrent ? t('Current') : t('Switch')}
        </Button>
      }
      title={label}
    />
  );
}

export default AnhingaEnvProfileRow;
